// F-WEB-020 /admin/audit — feed de cartoes paginado (5 colunas eliminadas,
// handoff §17: "a tabela de 5 colunas foi eliminada") + filtros + diff modal.
// Redesign 2026-09-22 (W2): entity_type/channel via FilterPanel (categoricos);
// action/since/until continuam livres (nao cabem no modelo de opcao unica do
// FilterPanel) — o botao "Aplicar" segue flushando os DOIS grupos juntos,
// preservando o comportamento existente ("filtros so aplicam ao clicar
// Aplicar", plano §6.4).
import { useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Eye } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import type { AuditEvent, AuditPageResponse, ChannelSummary } from "../../lib/types";
import { PageHeader } from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import { FilterPanel, type FilterGroup } from "../../components/ui/FilterPanel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";
import { formatDate } from "../../lib/utils";

interface Filters {
  entity_type: string;
  channel: string;
  action: string;
  since: string;
  until: string;
}

const EMPTY_FILTERS: Filters = {
  entity_type: "",
  channel: "",
  action: "",
  since: "",
  until: "",
};

const ENTITY_TYPES = [
  "entry",
  "publication",
  "category",
  "tag",
  "user",
  "user_channel",
  "settings",
  "channel",
  "channel_publication",
];

export function AdminAuditPage(): JSX.Element {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [detail, setDetail] = useState<AuditEvent | null>(null);

  const channelsQ = useQuery<ChannelSummary[]>({
    queryKey: ["channels"],
    queryFn: async () => (await api.get<ChannelSummary[]>("/api/channels")).data,
  });

  const query = useInfiniteQuery<AuditPageResponse>({
    queryKey: ["admin-audit", applied],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (typeof pageParam === "string") params.set("cursor", pageParam);
      if (applied.entity_type) params.set("entity_type", applied.entity_type);
      if (applied.channel) params.set("channel", applied.channel);
      if (applied.action) params.set("action", applied.action);
      if (applied.since) params.set("since", applied.since);
      if (applied.until) params.set("until", applied.until);
      const r = await api.get<AuditPageResponse>(`/api/admin/audit?${params.toString()}`);
      return r.data;
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const events: AuditEvent[] = (query.data?.pages ?? []).flatMap((p) => p.items);

  const groups: FilterGroup[] = [
    {
      key: "entity_type",
      label: "Entidade",
      options: ENTITY_TYPES.map((v) => ({ value: v, label: v })),
    },
    {
      key: "channel",
      label: "Canal",
      options: (channelsQ.data ?? []).map((c) => ({ value: c.slug, label: c.name })),
    },
  ];

  const filterValue = { entity_type: filters.entity_type || null, channel: filters.channel || null };

  return (
    <div>
      <PageHeader
        title="Log de auditoria"
        description="Todos os eventos imutáveis de mutação no sistema (RN05)."
      />

      <div className="with-filters">
        <FilterPanel
          groups={groups}
          value={filterValue}
          onChange={(key, value) => setFilters((s) => ({ ...s, [key]: value ?? "" }))}
          onClear={() => {
            setFilters(EMPTY_FILTERS);
            setApplied(EMPTY_FILTERS);
          }}
        >
          {/* Ação/Desde/Até nao cabem no modelo de opcao unica do
              FilterPanel (texto livre + intervalo de data) — moradas aqui
              (nao mais num Card avulso sempre visivel) pra nao quebrar o
              padrao "um so botao Filtros" no mobile (achado do smoke
              2026-09-22: essa Card empurrava a lista inteira pra baixo so
              nesta tela). "Aplicar" continua flushando os DOIS grupos
              juntos (plano §6.4). */}
          <div className="stack" style={{ gap: ".75rem", paddingTop: ".5rem", borderTop: ".0625rem solid var(--color-line)" }}>
            <div className="field">
              <Label htmlFor="f-action">Ação</Label>
              <Input
                id="f-action"
                placeholder="create, update, delete..."
                value={filters.action}
                onChange={(e) => setFilters((s) => ({ ...s, action: e.target.value }))}
              />
            </div>
            <div className="field">
              <Label htmlFor="f-since">Desde</Label>
              <Input
                id="f-since"
                type="datetime-local"
                value={filters.since}
                onChange={(e) => setFilters((s) => ({ ...s, since: e.target.value }))}
              />
            </div>
            <div className="field">
              <Label htmlFor="f-until">Até</Label>
              <Input
                id="f-until"
                type="datetime-local"
                value={filters.until}
                onChange={(e) => setFilters((s) => ({ ...s, until: e.target.value }))}
              />
            </div>
            <Button className="grow" onClick={() => setApplied(filters)}>
              Aplicar
            </Button>
          </div>
        </FilterPanel>

        <div className="with-filters__body">
          {query.isLoading ? (
            <Card><CardContent className="stack"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></CardContent></Card>
          ) : query.isError ? (
            <EmptyState title="Erro" description={apiErrorMessage(query.error)} />
          ) : events.length === 0 ? (
            <EmptyState title="Nenhum evento" description="Ajuste os filtros ou aguarde uma mutação." />
          ) : (
            <div className="stack">
              {events.map((e) => (
                <div key={e.id} className="row-card">
                  <div className="row-card__body">
                    <div className="spread">
                      <span className="cluster">
                        <span className="chip chip--mono">{e.action}</span>
                        <span className="mono" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
                          {formatDate(e.created_at)}
                        </span>
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => setDetail(e)} aria-label="Ver diff">
                        <Eye className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden />
                      </Button>
                    </div>
                    <div className="stack" style={{ gap: ".125rem" }}>
                      <span style={{ fontWeight: 800 }}>
                        {e.actor?.name ?? <span style={{ color: "var(--color-ink-2)", fontWeight: 600 }}>sistema</span>}
                      </span>
                      <span className="mono" style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
                        {e.entity_type} · {e.entity_id}
                      </span>
                      <span style={{ fontSize: "var(--text-sm)", color: "var(--color-ink-2)" }}>
                        Canal: {e.channel_slug ?? "—"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {query.hasNextPage ? (
                <Button
                  variant="secondary"
                  onClick={() => query.fetchNextPage()}
                  loading={query.isFetchingNextPage}
                >
                  Carregar mais
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {detail ? (
        <Dialog open onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Evento #{detail.id}</DialogTitle>
              <DialogDescription>
                {formatDate(detail.created_at)} · {detail.actor?.name ?? "sistema"} ·{" "}
                <span className="chip chip--mono">{detail.action}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="cards">
              <DiffPane title="Antes" data={detail.before} />
              <DiffPane title="Depois" data={detail.after} />
            </div>
            <div className="field__hint">
              Request: <code className="mono">{detail.request_id ?? "—"}</code>
              {detail.ip ? <> · IP: <code className="mono">{detail.ip}</code></> : null}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="secondary">Fechar</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function DiffPane({ title, data }: { title: string; data: unknown }): JSX.Element {
  return (
    <div>
      <div className="field__hint" style={{ fontWeight: 800, marginBottom: ".25rem" }}>{title}</div>
      <pre
        className="mono"
        style={{
          fontSize: "var(--text-sm)",
          background: "var(--color-soft)",
          border: ".0625rem solid var(--color-line)",
          borderRadius: "var(--radius-md)",
          padding: ".5rem",
          maxHeight: "20rem",
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {data == null ? <span style={{ color: "var(--color-ink-2)" }}>—</span> : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}
